/**
 * Which numbers are on the do-not-call list, for everything that prints one.
 *
 * A set held once for the whole shell rather than a lookup per number. The list
 * is small by nature - it grows only when somebody says "stop calling" - and the
 * alternative is every phone number on screen making its own request, or worse,
 * nothing being struck through until the operator has already dialled and been
 * refused.
 *
 * Its own context rather than more state inside `CallProvider`, which was split
 * once already for exactly this reason: a poll plus three network actions plus
 * three refs is what pushed it past the point where anybody could read it.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { listPhoneSuppression } from "../../lib/callSuppression";

export interface SuppressedNumbers {
  /** E.164 values. The same key `normalizeE164` produces, on both sides. */
  has: (e164: string | null | undefined) => boolean;
  /**
   * How many numbers are on the list.
   *
   * Read off the same set rather than counted again: `add` and `remove` below
   * keep it in step, so the badge on the call rail cannot disagree with the
   * screen the badge leads to.
   */
  count: number;
  /** After marking a number, so the strike-through appears without a refetch. */
  add: (e164: string) => void;
  remove: (e164: string) => void;
}

export const SuppressedNumbersContext = createContext<SuppressedNumbers | null>(null);

/**
 * Nullable on purpose, the same shape `useCallContext` has: a screen mounted
 * outside the shell (a test, a preview) simply strikes nothing through instead
 * of failing to render.
 */
export function useSuppressedNumbers(): SuppressedNumbers | null {
  return useContext(SuppressedNumbersContext);
}

export function useSuppressedNumbersState(enabled: boolean): SuppressedNumbers {
  const [values, setValues] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    listPhoneSuppression()
      .then((res) => {
        if (alive) setValues(new Set(res.rows.map((r) => r.value)));
      })
      // A list that failed to load must not break the page it was going to
      // decorate. The dial itself is guarded on the server, so the cost of
      // missing this is a number that is not struck through, not a call that
      // should not have happened.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enabled]);

  const has = useCallback((e164: string | null | undefined) => !!e164 && values.has(e164), [values]);
  const add = useCallback((e164: string) => setValues((cur) => new Set(cur).add(e164)), []);
  const remove = useCallback(
    (e164: string) =>
      setValues((cur) => {
        const next = new Set(cur);
        next.delete(e164);
        return next;
      }),
    [],
  );

  return useMemo(() => ({ has, count: values.size, add, remove }), [has, values.size, add, remove]);
}
